import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import {
  BehaviorSubject,
  combineLatest,
  Observable,
  of,
  ReplaySubject,
  Subject,
  Subscription,
  distinctUntilChanged,
  filter,
  map,
  startWith,
  switchMap,
  take,
  withLatestFrom,
} from 'rxjs';
import { Store } from '@ngrx/store';

import { unsubscribe } from '@fcom/core/utils';
import {
  AncillarySubviewComponent,
  ConfirmModalTranslationKeys,
  SmpProduct,
} from '@fcom/common-booking/modules/ancillaries/components';
import { GtmService } from '@fcom/common/gtm';
import {
  ElementActions,
  ElementTypes,
  GaContext,
  GtmEcommerceMeasurement,
  GtmEvent,
  GtmPurchaseFlow,
  GtmTripType,
  ToasterService,
} from '@fcom/common';
import {
  Category,
  FinnairBoundItem,
  FinnairItineraryItemFlight,
  FinnairOrder,
  FinnairPassengerItem,
  FinnairServiceBoundItem,
  FinnairServiceItem,
  PaxLevelQuota,
  SeatmapSvgAndControlData,
} from '@fcom/dapi/api/models';
import {
  BOUND_BASED_CATEGORIES,
  buttonTexts,
  changedServices,
  getBoundsAndTravelersWhichHavePetcIncompatibleSeats,
  getQuantitiesForFragmentsAndPassenger,
  isAllowedToSell,
  isBoundBasedCategory,
  isJourneyBasedCategory,
  isSeatPurchaseClosedAfterCheckInForFlight,
  PETC_CONFIRM_MODAL_TRANSLATION_KEYS,
} from '@fcom/common-booking/modules/ancillaries/utils';
import {
  Amount,
  SelectedServiceData,
  ServiceAvailability,
  ServiceCatalogCategoryServicesMap,
  ServiceCatalogSelectionsForAllTravelers,
  ServiceCatalogServices,
  ServicesStatus,
} from '@fcom/dapi';
import { SeatMapStatus } from '@fcom/common/interfaces/seat-map.interface';
import {
  BookingSummaryService,
  combinedServicesMap,
  currentServices,
  getOrderPassengers,
  includedServices,
  includedServicesMap,
  includedVariantsQuantity,
  OrderPartStatus,
  OrderStatus,
  orderStatus,
  QuantityForFragmentAndPassenger,
  SeatMapActions,
  seatMapForFlight,
  seatMapForFlightStatus,
  seatMapWithStatus,
  seatSelections,
  serviceAvailability,
  serviceCatalog,
  serviceCatalogCampaignIdsForFragment,
  serviceCategoriesMap,
  servicesStatus,
  updatingOrderPart,
  variantsQuantity,
} from '@fcom/common-booking';
import { getServiceData } from '@fcom/common-booking/utils/ancillaries.utils';
import { BookingAppState } from '@fcom/common/interfaces/booking/booking-state.interface';
import { finShare, safeMap } from '@fcom/rx';
import { ButtonPosition, NotificationTheme } from '@fcom/ui-components';
import { LanguageService } from '@fcom/ui-translate';
import { RootPaths } from '@fcom/core';
import {
  isBoundOrFlightConfirmedOrCheckedIn,
  isFlight,
  isNotIncludedService,
  toAmount,
} from '@fcom/common-booking/utils/common-booking.utils';
import {
  ConfirmModal,
  ConfirmModalData,
  EligibilitiesForFragments,
  PurchasedService,
  PurchasedServicesForOrder,
  SeatChangeEvent,
} from '@fcom/common-booking/modules/ancillaries/interfaces';
import { getTripTypeFromBounds } from '@fcom/common/utils/gtm.utils';
import { MANAGE_BOOKING_STEPS } from '@fcom/common-booking/manage-booking-config';
import { AncillaryLoaderStatus } from '@fcom/common-booking/modules/ancillaries/enums';

import { ManageBookingService } from '../../../../services';
import { filterFlightsFromAllBounds } from '../../../../utils';

interface FinnairItineraryItemFlightWithItineraries extends FinnairItineraryItemFlight {
  seatMap$: Observable<SeatmapSvgAndControlData>;
  seatMapStatus$: Observable<SeatMapStatus>;
  seatSubViewTopNotificationTranslation$?: Observable<string>;
}

const getServicesForBound = (bound: FinnairServiceBoundItem): PurchasedService[] => {
  return bound.segments
    .reduce((allSegments, segment) => {
      const segmentServiceItems = segment.passengers.flatMap((passenger) => {
        return passenger.services.filter(isNotIncludedService).reduce((purchasedServices, service) => {
          const existingService = purchasedServices.find(
            (s) => s.variant === service.variant && s.maxBaggageWeight === service.parameters?.baggageWeight
          );
          if (existingService) {
            existingService.quantity += service.quantity;
          } else {
            purchasedServices.push({
              title: service.displayName || service.variant,
              ...(service.seatNumber ? { seatNumber: service.seatNumber } : {}),
              variant: service.variant,
              category: service.subCategory,
              quantity: service.quantity,
              travelerId: passenger.id,
              maxBaggageWeight: service.parameters?.baggageWeight,
            });
          }

          return purchasedServices;
        }, [] as PurchasedService[]);
      });

      return segmentServiceItems.reduce((allServiceItems, service) => {
        const existingService = allServiceItems.find(
          (serviceItem) =>
            serviceItem.travelerId === service.travelerId &&
            serviceItem.variant === service.variant &&
            serviceItem.maxBaggageWeight === service.maxBaggageWeight
        );
        if (existingService) {
          existingService.quantity += service.quantity;
        } else {
          allServiceItems.push(service);
        }

        return allServiceItems;
      }, allSegments);
    }, [] as PurchasedService[])
    .map((item) => {
      return {
        ...item,
        quantity: item.quantity / bound.segments.length,
      };
    });
};

const getSegmentServices = (bound: FinnairServiceBoundItem): PurchasedServicesForOrder => {
  return bound.segments.reduce((segments, segment) => {
    segments[segment.id] = segment.passengers.flatMap((passenger) =>
      passenger.services.filter(isNotIncludedService).map((service) => {
        return {
          title: service.displayName || service.variant,
          category: service.subCategory,
          quantity: service.quantity,
          seatNumber: service.seatNumber,
          variant: service.variant,
          travelerId: passenger.id,
        };
      })
    );

    return segments;
  }, {} as PurchasedServicesForOrder);
};

@Component({
  selector: 'fin-mmb-ancillaries',
  templateUrl: './ancillaries.component.html',
  styleUrls: ['./ancillaries.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ManageBookingAncillariesComponent implements OnInit, OnDestroy, AfterViewInit {
  readonly purchaseFlow = GtmPurchaseFlow.MANAGE_BOOKING;
  readonly TRACKING_CONTEXT = GaContext.MANAGE_BOOKING;
  readonly Category = Category;
  readonly buttonText = buttonTexts;
  readonly ButtonPosition = ButtonPosition;
  readonly BOUND_BASED_CATEGORIES = BOUND_BASED_CATEGORIES;
  readonly PETC_CONFIRM_MODAL_TRANSLATION_KEYS = PETC_CONFIRM_MODAL_TRANSLATION_KEYS;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly NotificationTheme = NotificationTheme;

  @ViewChild('ancillarySubview', { static: false })
  ancillarySubview: AncillarySubviewComponent;

  @Input() products$: Observable<SmpProduct[]>;
  @Input() order$: Observable<FinnairOrder>;
  @Input() openAncillarySubview$: Observable<SelectedServiceData>;

  @Output() emailModalOpen: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() scrollToTravelExtras: EventEmitter<void> = new EventEmitter<void>();

  pax$: Observable<FinnairPassengerItem>;
  servicesMap$: Observable<ServiceCatalogCategoryServicesMap>;
  currentServices$: Observable<ServiceCatalogServices>;
  priceUpdating$: Observable<boolean>;
  hasUnpaidServices$: Observable<boolean>;
  includedServices$: Observable<Record<string, FinnairServiceItem>>;

  combinedServices$: Observable<Record<string, FinnairServiceItem>>;
  currentIncludedServices$: Observable<FinnairServiceItem>;
  currentCombinedServices$: Observable<FinnairServiceItem>;
  availability$: Observable<ServiceAvailability>;
  totalPrice$: Observable<Amount>;
  boundsOrFlights$: Observable<Array<FinnairBoundItem | FinnairItineraryItemFlight>>;
  selectedCategory$: BehaviorSubject<Category>;
  unpaidServices$: Observable<BookingSummaryService[]>;
  subtitleMessageKey$: Observable<string>;
  boundOrFlightAncillaryAvailability$: Observable<EligibilitiesForFragments>;
  sideBarOpen$: Subject<boolean> = new BehaviorSubject(false);
  purchasedServices$: Observable<PurchasedServicesForOrder>;
  flights$: Observable<FinnairItineraryItemFlight[]>;
  passengers$: Observable<FinnairPassengerItem[]>;
  itineraryFlights$: Observable<FinnairItineraryItemFlightWithItineraries[]>;
  seatPurchaseNotEligibleAfterCheckInForFlight$: Observable<Map<string, boolean>>;
  subTotalPrice$: Observable<Amount>;
  maxedOutForFragmentId$: Observable<Record<string, boolean>>;
  tripType$: Observable<GtmTripType>;
  viewDetailsClosed$: Subject<Category> = new Subject();
  focusOnProductBtn$: Subject<Category> = new Subject();
  selectedFlightOrBound$: BehaviorSubject<string> = new BehaviorSubject(null);
  servicesReady$: Observable<boolean>;
  passengersWithPetcUnpaidIncompatibleSeats$: Observable<Record<string, string[]>>;
  blockSelectionNotification$: Observable<Record<string, string[]>>;
  confirmModalData$ = new BehaviorSubject<ConfirmModal | undefined>({
    open: false,
  });
  serviceQuantitityMap$: Observable<QuantityForFragmentAndPassenger>;
  paxLevelQuota$: Observable<PaxLevelQuota>;
  seatSelections$: Observable<ServiceCatalogSelectionsForAllTravelers>;
  initialSelectedTravelerId$: ReplaySubject<string> = new ReplaySubject(1);
  viewReady$: Subject<boolean> = new Subject();

  ancillarySubviewOpen$: Subject<boolean> = new BehaviorSubject(false);
  private subscriptions: Subscription = new Subscription();

  constructor(
    private gtmService: GtmService,
    private store$: Store<BookingAppState>,
    private manageBookingService: ManageBookingService,
    private router: Router,
    private languageService: LanguageService,
    private toasterService: ToasterService
  ) {}

  viewDetails(category: Category): void {
    this.selectedCategory$.next(category);
    this.sideBarOpen$.next(true);
  }

  showSubview(category: Category, slideIndexToOpen: number): void {
    this.ancillarySubviewOpen$.next(true);
    this.selectedCategory$.next(category);
    this.ancillarySubview.open(null, slideIndexToOpen);
    this.sideBarOpen$.next(false);
  }

  closeSidebar(): void {
    this.viewDetailsClosed$.next(this.selectedCategory$.value);
    this.sideBarOpen$.next(false);
  }

  openAncillarySubview({ category, index, fragmentId, paxId }: SelectedServiceData): void {
    this.subscriptions.add(
      this.pax$.pipe(take(1), withLatestFrom(this.order$)).subscribe(([passengerItem, order]) => {
        if (!passengerItem?.email) {
          this.emailModalOpen.emit(true);
        } else {
          const boundIndex = order.bounds.findIndex((b) => b.id === fragmentId);
          const segmentIndex = order.bounds
            .flatMap((b) => b.itinerary.filter(isFlight))
            .findIndex((segment) => segment.id === fragmentId);

          this.initialSelectedTravelerId$.next(paxId);

          if (typeof index === 'number') {
            this.showSubview(category, index);
          } else if (isBoundBasedCategory(category)) {
            const boundIndexToGo =
              boundIndex === -1
                ? order.bounds.findIndex((b) => b.itinerary.filter(isFlight).some((i) => i.id === fragmentId))
                : boundIndex;
            this.showSubview(category, boundIndexToGo);
          } else {
            const segmentToGo = order.bounds
              .map((b, i) => (i < boundIndex ? b.itinerary.filter(isFlight).length : 0))
              .reduce((all, size) => all + size, 0);

            this.showSubview(category, boundIndex === -1 ? segmentIndex : segmentToGo);
          }
        }
      })
    );
  }

  openAncillarySubviewFromCampaign(categories: Category[]): void {
    if (categories.length === 1) {
      this.openAncillarySubview({ category: categories[0], index: 0 });
    } else {
      this.scrollToTravelExtras.emit();
    }
  }

  ngOnInit(): void {
    this.pax$ = this.order$.pipe(map((booking) => booking.passengers[0]));
    this.selectedCategory$ = new BehaviorSubject<Category>(undefined);
    this.unpaidServices$ = this.store$.pipe(currentServices());
    this.availability$ = this.store$.pipe(serviceAvailability());
    this.servicesMap$ = this.store$.pipe(serviceCategoriesMap());
    this.paxLevelQuota$ = this.store$.pipe(serviceCatalog()).pipe(
      map((catalog) => catalog.paxLevelQuota),
      distinctUntilChanged(),
      finShare()
    );

    this.serviceQuantitityMap$ = this.paxLevelQuota$.pipe(
      switchMap(({ variants }) => {
        return combineLatest([
          this.store$.pipe(variantsQuantity(variants)),
          this.store$.pipe(includedVariantsQuantity(variants)),
          this.order$,
        ]).pipe(
          map(([selectedQuantities, includedQuantities, order]) =>
            getQuantitiesForFragmentsAndPassenger(selectedQuantities, includedQuantities, order)
          )
        );
      })
    );
    this.currentServices$ = this.selectedCategory$.pipe(
      switchMap((currentCategory) => this.servicesMap$.pipe(map((services) => services?.[currentCategory])))
    );

    this.flights$ = this.order$.pipe(map((booking) => filterFlightsFromAllBounds(booking)));

    this.passengers$ = combineLatest([
      this.order$.pipe(map(({ passengers }) => passengers)),
      this.store$.pipe(getOrderPassengers()),
    ]).pipe(map(([passengers, passengerIds]) => passengers.filter(({ id }) => passengerIds.includes(id))));

    this.tripType$ = this.order$.pipe(
      filter(Boolean),
      map((booking: FinnairOrder) => getTripTypeFromBounds(booking.bounds))
    );

    this.priceUpdating$ = this.store$.pipe(
      updatingOrderPart(),
      map(
        (statuses) =>
          !!statuses &&
          Object.keys(statuses).some((category: Category) => statuses[category] === OrderPartStatus.UPDATING)
      ),
      startWith(false)
    );

    this.servicesReady$ = combineLatest([
      this.store$.pipe(
        servicesStatus(),
        map((status) => status === ServicesStatus.OK)
      ),
      this.priceUpdating$,
    ]).pipe(map(([servicesReady, priceUpdating]) => servicesReady && !priceUpdating));

    this.boundsOrFlights$ = this.selectedCategory$.pipe(
      filter(Boolean),
      switchMap((category) => {
        if (isJourneyBasedCategory(category)) {
          return of([]);
        } else if (isBoundBasedCategory(category)) {
          return this.order$.pipe(map((b) => b.bounds));
        } else {
          return this.flights$;
        }
      }),
      startWith([]),
      finShare()
    );

    this.totalPrice$ = this.order$.pipe(
      safeMap((data: FinnairOrder) => data.prices),
      safeMap((prices) => toAmount(prices.unpaid.total.total.totalAmount)),
      distinctUntilChanged(),
      finShare()
    );

    this.includedServices$ = this.store$.pipe(includedServicesMap(), finShare());
    this.combinedServices$ = this.store$.pipe(combinedServicesMap(), finShare());

    this.currentIncludedServices$ = this.selectedCategory$.pipe(
      switchMap((currentCategory) =>
        this.includedServices$.pipe(
          map((services) => services?.[currentCategory]),
          finShare()
        )
      )
    );
    this.subtitleMessageKey$ = this.currentIncludedServices$.pipe(
      withLatestFrom(this.selectedCategory$),
      filter(([_, selectedCategory]) => selectedCategory === Category.SEAT),
      map(
        ([currentIncludedServices]) =>
          !currentIncludedServices?.bounds?.some((bound) =>
            bound.segments.some((segment) =>
              segment.passengers.some((passenger) => passenger.services.some((service) => !!service.seatNumber))
            )
          )
      ),
      filter((noSeatNumber) => !!noSeatNumber),
      map(() => 'ancillaries.seats.usDotMessage')
    );
    this.currentCombinedServices$ = this.selectedCategory$.pipe(
      switchMap((currentCategory) =>
        this.combinedServices$.pipe(
          map((services) => services?.[currentCategory]),
          finShare()
        )
      )
    );

    const serviceBoundItemsForCategory$ = this.selectedCategory$.pipe(
      switchMap((category) =>
        this.store$.pipe(
          includedServices(category),
          safeMap((serviceItems) => serviceItems.flatMap((item) => item.bounds))
        )
      )
    );

    this.purchasedServices$ = combineLatest([serviceBoundItemsForCategory$, this.selectedCategory$]).pipe(
      safeMap(([items, category]) => {
        return items.reduce((allFragments, bound) => {
          if (isBoundBasedCategory(category)) {
            allFragments[bound.id] = getServicesForBound(bound);
          } else {
            return {
              ...allFragments,
              ...getSegmentServices(bound),
            };
          }
          return allFragments;
        }, {} as PurchasedServicesForOrder);
      })
    );

    this.boundOrFlightAncillaryAvailability$ = combineLatest([
      this.availability$,
      this.boundsOrFlights$,
      this.selectedCategory$,
      this.order$,
    ]).pipe(
      map(([availability, boundsOrFlights, category, booking]) => {
        return boundsOrFlights.reduce((all, boundOrFlight) => {
          const isConfirmedOrCheckedIn = isBoundOrFlightConfirmedOrCheckedIn(boundOrFlight);
          const allowedToSell = isAllowedToSell(category, booking, availability, boundOrFlight);

          all[boundOrFlight.id] = allowedToSell.map((eligibility) => {
            return {
              ...eligibility,
              isAllowedToUse: isConfirmedOrCheckedIn && eligibility.isAllowedToUse,
            };
          });
          return all;
        }, {});
      }),
      distinctUntilChanged(),
      finShare()
    );

    this.itineraryFlights$ = this.flights$.pipe(
      map((data) =>
        data.flatMap((flight) => ({
          ...flight,
          seatMap$: this.store$.pipe(seatMapForFlight(flight.id)),
          seatMapStatus$: combineLatest([
            this.store$.pipe(seatMapForFlightStatus(flight.id)),
            this.priceUpdating$,
            this.store$.pipe(orderStatus()),
          ]).pipe(
            map(([seatMapStatus, priceUpdating, os]) =>
              priceUpdating || os === OrderStatus.PENDING
                ? SeatMapStatus.PENDING
                : seatMapStatus || SeatMapStatus.INITIAL
            )
          ),
          seatSubViewTopNotificationTranslation$: this.store$.pipe(
            serviceCatalogCampaignIdsForFragment(flight.id),
            filter((campaigns) => campaigns && campaigns[Category.SEAT] && campaigns[Category.SEAT].length > 0),
            map((campaigns) => `campaign.notifications.seatSubView.${campaigns[Category.SEAT][0]}`),
            switchMap((translationKey) => this.languageService.translate(translationKey))
          ),
        }))
      )
    );

    const product$ = combineLatest([this.products$, this.selectedCategory$]).pipe(
      map(([products, category]) => products.find((p) => p.category === category)),
      filter(Boolean)
    );
    this.maxedOutForFragmentId$ = product$.pipe(switchMap((product) => product.purchasesDoneForFragment$));
    this.subTotalPrice$ = product$.pipe(switchMap((product) => product.price$));

    this.hasUnpaidServices$ = this.order$.pipe(map((order) => order?.services?.unpaid?.length > 0));

    const selectedFlight$ = this.selectedFlightOrBound$.pipe(
      switchMap((selectedFlightOrBound) =>
        this.flights$.pipe(map((flights) => flights.find((fl) => fl.id === selectedFlightOrBound)))
      ),
      filter(Boolean)
    );
    this.subscriptions.add(
      this.selectedFlightOrBound$
        .pipe(
          switchMap((selectedFlight) => this.store$.pipe(seatMapWithStatus(selectedFlight), take(1))),
          withLatestFrom(this.selectedCategory$, this.ancillarySubviewOpen$),
          filter(
            ([seatMapWithStatus, selectedCategory, ancillarySubviewOpen]) =>
              selectedCategory === Category.SEAT &&
              (!seatMapWithStatus ||
                ![SeatMapStatus.PENDING, SeatMapStatus.OK, SeatMapStatus.OK_NO_AVAILABILITY].includes(
                  seatMapWithStatus.status
                )) &&
              ancillarySubviewOpen
          ),
          switchMap(() => combineLatest([selectedFlight$, this.order$]).pipe(take(1)))
        )
        .subscribe(([selectedFlightOrBound, order]: [FinnairItineraryItemFlight, FinnairOrder]) => {
          this.loadSeatMapForFlight(selectedFlightOrBound, order);
        })
    );

    this.passengersWithPetcUnpaidIncompatibleSeats$ = this.order$.pipe(
      map((booking) => getBoundsAndTravelersWhichHavePetcIncompatibleSeats(booking, 'unpaid')),
      finShare()
    );
    this.blockSelectionNotification$ = this.order$.pipe(
      map((booking) => getBoundsAndTravelersWhichHavePetcIncompatibleSeats(booking, 'included')),
      finShare()
    );

    this.subscriptions.add(
      this.viewReady$
        .pipe(switchMap(() => this.openAncillarySubview$))
        .subscribe((data) => this.openAncillarySubview(data))
    );
    this.seatSelections$ = this.store$.pipe(seatSelections());
    this.seatPurchaseNotEligibleAfterCheckInForFlight$ = this.order$.pipe(
      map((order) => {
        const seatEligibilities = order.eligibilities?.serviceCatalog?.categories?.find(
          (c) => c.category === Category.SEAT
        );
        if (!seatEligibilities || !seatEligibilities.services) {
          return new Map<string, boolean>();
        }
        const mapForFlight = new Map<string, boolean>();
        Object.keys(seatEligibilities.services).forEach((flightId) => {
          mapForFlight.set(flightId, isSeatPurchaseClosedAfterCheckInForFlight(order, flightId));
        });
        return mapForFlight;
      })
    );
  }

  ngAfterViewInit(): void {
    this.viewReady$.next(true);
  }

  loadSeatMapForFlightFromOrder(flight: FinnairItineraryItemFlight): void {
    this.subscriptions.add(this.order$.pipe(take(1)).subscribe((order) => this.loadSeatMapForFlight(flight, order)));
  }

  setConfirmModalData(
    confirmData: ConfirmModalData,
    confirmationModalTranslationKeys: ConfirmModalTranslationKeys
  ): void {
    this.confirmModalData$.next({
      open: true,
      confirmData: {
        ...confirmData,
        confirmationModalTranslationKeys: confirmationModalTranslationKeys,
      },
    });
  }

  closeConfirmModal(confirmData?: ConfirmModalData): void {
    this.confirmModalData$.next({
      open: false,
      confirmData: {
        variant: confirmData?.variant,
        flightOrBoundId: confirmData?.flightOrBoundId,
        travelerId: confirmData?.travelerId,
      },
    });
  }

  changeSeats({ travelerId, flightId, seatNumber }: SeatChangeEvent): void {
    this.subscriptions.add(
      this.itineraryFlights$
        .pipe(
          take(1),
          map((flights) => {
            return flights.find((f) => f.id === flightId);
          }),
          switchMap((flight) => {
            return flight.seatMap$;
          })
        )
        .subscribe((seatMap) => {
          const selectedSeat = seatMap.seats.find((seat) => seat.seatNumber === seatNumber);

          this.store$.dispatch(
            SeatMapActions.setSeatForFlight({
              flightId,
              travelerId,
              seat: selectedSeat,
            })
          );
        })
    );
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscriptions);
  }

  private loadSeatMapForFlight(flight: FinnairItineraryItemFlight, order: FinnairOrder): void {
    this.manageBookingService.triggerFetchSeatMap(flight, order);
  }

  updateServices(booking: FinnairOrder, category: Category): void {
    this.ancillarySubviewOpen$.next(false);
    this.focusOnProductBtn$.next(category);
    this.confirmModalData$.next({ open: false });

    this.sendServiceUpdate(booking, category);
  }

  updateServicesAndRedirect(booking: FinnairOrder, category: Category): void {
    this.sendServiceUpdate(booking, category);

    this.closeAndContinueToPurchase(category);
  }

  trackChanges(orderBeforeUpdate: FinnairOrder, orderAfterUpdate: FinnairOrder, category: Category): void {
    const { addedServices, removedServices, addedProducts, removedProducts } = changedServices(
      orderBeforeUpdate,
      orderAfterUpdate,
      category,
      GtmPurchaseFlow.MANAGE_BOOKING
    );

    if (addedServices.length) {
      this.gtmService.ecommerceEvent(
        GtmEvent.ADD_SERVICES,
        GtmEcommerceMeasurement.ADD,
        addedServices,
        GtmPurchaseFlow.MANAGE_BOOKING,
        orderAfterUpdate?.prices.unpaid.total.total.totalAmount.currencyCode
      );
    }
    if (addedProducts.length) {
      this.gtmService.ecommerceEventGA4(GtmEvent.ADD_TO_CART, addedProducts);
    }
    if (removedServices.length) {
      this.gtmService.ecommerceEvent(
        GtmEvent.REMOVE_SERVICES,
        GtmEcommerceMeasurement.REMOVE,
        removedServices,
        GtmPurchaseFlow.MANAGE_BOOKING
      );
    }
    if (removedProducts.length) {
      this.gtmService.ecommerceEventGA4(GtmEvent.REMOVE_FROM_CART, removedProducts);
    }
  }

  continuePurchase(): void {
    this.gtmService.trackElement(
      'continue-to-review_view-cta',
      GaContext.MANAGE_BOOKING,
      ElementTypes.BUTTON,
      undefined,
      ElementActions.CLICK
    );
    this.router.navigateByUrl(
      `/${this.languageService.langValue}/${RootPaths.MANAGE_BOOKING_ROOT}/${MANAGE_BOOKING_STEPS.REVIEW.path}`
    );
  }

  flightOrBoundSelected(flight: FinnairBoundItem | FinnairItineraryItemFlight): void {
    this.selectedFlightOrBound$.next(flight?.id);
  }

  reassignFreeSeatsForFlight(booking: FinnairOrder, flightInformation: { currentFlightId: string }): void {
    this.subscriptions.add(
      this.passengers$.pipe(take(1)).subscribe((passengers) => {
        this.ancillarySubview.ancillaryLoader$.next(AncillaryLoaderStatus.REASSIGNING);
        const flight: FinnairItineraryItemFlight = booking.bounds
          .flatMap((b) => b.itinerary)
          .filter(isFlight)
          .find((flight) => flight.id === flightInformation.currentFlightId);
        passengers.forEach((passenger) => {
          this.store$.dispatch(
            SeatMapActions.setSeatForFlight({
              flightId: flight.id,
              travelerId: passenger.id,
              seat: {},
            })
          );
        });

        const [requests, messages] = getServiceData(this.store$, Category.SEAT, booking.bounds, [flight]);
        this.manageBookingService.setServices(requests, Category.SEAT, {
          next: (order) => {
            this.loadSeatMapForFlight(flight, order);
            this.trackChanges(booking, order, Category.SEAT);
          },
          error: () => {
            this.toasterService.addMessageToQueue(messages.error);
          },
        });
      })
    );
  }

  private sendServiceUpdate(booking: FinnairOrder, category: Category) {
    const flights = booking.bounds.reduce(
      (allFlights, bound) => allFlights.concat(bound.itinerary.filter(isFlight)),
      []
    );
    const [requests, messages] = getServiceData(this.store$, category, booking.bounds, flights);

    this.manageBookingService.setServices(requests, category, {
      next: (order) => {
        this.trackChanges(booking, order, category);
      },
      error: () => {
        this.toasterService.addMessageToQueue(messages.error);
      },
    });
  }

  private closeAndContinueToPurchase(category: Category): void {
    this.ancillarySubview.subview?.closeSubview(false);
    this.ancillarySubviewOpen$.next(false);
    this.focusOnProductBtn$.next(category);
    this.confirmModalData$.next({ open: false });
    this.continuePurchase();
  }
}
